import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import type { AddBookState } from '../../hooks/useAddBook'
import ErrorMessage from '../ErrorMessage'

export default function ManualChaptersForm({ ab }: { ab: AddBookState }) {
  const navigate = useNavigate()
  const {
    title, author, chapters, selectedBook, submitting, error, setMode, handleImport, addChapter, removeChapter, updateChapter,
  } = ab

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/library')} sx={{ mb: 2 }}>
        Back to Library
      </Button>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>Add Book</Typography>

      <Box component="form" onSubmit={handleImport}>
        <Card elevation={0} variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              <strong>{title}</strong> by {author}
            </Typography>
            <Button size="small" onClick={() => setMode(selectedBook ? 'search' : 'manual')}>Change</Button>
          </CardContent>
        </Card>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add chapters and paste your highlights below (one per line). Highlights are optional — skip them to generate questions based on the book alone.
        </Typography>

        <Stack spacing={2} sx={{ mb: 2 }}>
          {chapters.map((ch, i) => (
            <Card key={i} elevation={1}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-end' }}>
                  <TextField
                    label="Chapter title"
                    value={ch.title}
                    onChange={e => updateChapter(i, 'title', e.target.value)}
                    placeholder={`Chapter ${i + 1}`}
                    size="small"
                    fullWidth
                  />
                  {chapters.length > 1 && (
                    <Button color="error" size="small" onClick={() => removeChapter(i)} sx={{ flexShrink: 0 }}>Remove</Button>
                  )}
                </Box>
                <TextField
                  label="Highlights (one per line)"
                  value={ch.highlightText}
                  onChange={e => updateChapter(i, 'highlightText', e.target.value)}
                  placeholder="Paste your highlights here, one per line..."
                  multiline
                  rows={6}
                  fullWidth
                  size="small"
                />
              </CardContent>
            </Card>
          ))}
        </Stack>

        <Button variant="text" onClick={addChapter} sx={{ mb: 2 }}>+ Add chapter</Button>

        {error && <ErrorMessage message={error} />}

        <Button variant="contained" type="submit" disabled={submitting} sx={{ mt: 1 }}>
          {submitting ? <CircularProgress size={18} /> : <span>Import book</span>}
        </Button>
      </Box>
    </Box>
  )
}
